import React, { useEffect, useState, useCallback } from "react";
import api, { RBAC_BASE } from "../../../api/rbacApi";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../../Redux/slice/features/authSlice";

const RolePermission = () => {
  const { roleId } = useParams();
  const [initialSelected, setInitialSelected] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [showAssigned, setShowAssigned] = useState(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userType = useSelector((state) => state.auth.userType);
  const currentUser = useSelector((state) => state.auth.user);

  const location = useLocation();
  const roleName = location.state?.roleName;
  const selectedPerms = permissions.filter((p) =>
    selected.includes(p.intPermissionId)
  );

  const load = useCallback(async () => {
    const [allPerms, rolePerms] = await Promise.all([
      api.get(`${RBAC_BASE}/permissions`),
      api.get(`${RBAC_BASE}/roles/${roleId}/permissions`)
    ]);

    const activePerms = allPerms.data.filter((p) => Boolean(p.bitStatus));
    setPermissions(activePerms);

    const validSelected = rolePerms.data.filter((id) =>
      activePerms.some((p) => p.intPermissionId === id)
    );

    setSelected(validSelected);
    setInitialSelected(validSelected);

    const collapsedDefault = {};
    activePerms.forEach((p) => {
      collapsedDefault[p.vchModule] = true;
    });

    setCollapsed(collapsedDefault);
    setShowAssigned(true);
  }, [roleId]);

  useEffect(() => {
    if (!roleId) return;
    load();
  }, [load, roleId]);

  const hasChanges =
    selected.length !== initialSelected.length ||
    selected.some((id) => !initialSelected.includes(id));

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const isAllSelected = selected.length === permissions.length;

  const handleGlobalToggle = () => {
    if (isAllSelected) {
      setSelected([]);
    } else {
      setSelected(permissions.map((p) => p.intPermissionId));
    }
  };

  const handleModuleToggle = (modulePerms) => {
    const ids = modulePerms.map((p) => p.intPermissionId);
    const allSelected = ids.every((id) => selected.includes(id));

    if (allSelected) {
      setSelected((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const save = async () => {
    if (!hasChanges) return;

    await api.post(`${RBAC_BASE}/roles/assign-permissions`, {
      roleId,
      permissions: selected
    });

    setInitialSelected(selected);

    alert("Permissions updated successfully");

    const currentRoleId = String(currentUser?.user?.roleId);

    if (currentRoleId === String(roleId)) {
      alert("Your permissions changed. You will be logged out.");

      const isEmployee = sessionStorage.getItem("hrmsLogin");
      const isHrmsPath = window.location.pathname.startsWith("/hrms");

      sessionStorage.removeItem("hrmsLogin");

      await dispatch(logoutUser());

      if (isEmployee || isHrmsPath || userType === "hrms") {
        navigate("/hrms/login", { replace: true });
      } else {
        navigate("/signin", { replace: true });
      }
    }
  };

  const grouped = permissions.reduce((acc, p) => {
    acc[p.vchModule] = acc[p.vchModule] || [];
    acc[p.vchModule].push(p);
    return acc;
  }, {});

  const handleExpandAll = () => {
    const expanded = {};
    Object.keys(grouped).forEach((module) => {
      expanded[module] = false;
    });
    setCollapsed(expanded);
  };

  const handleCollapseAll = () => {
    const collapsedAll = {};
    Object.keys(grouped).forEach((module) => {
      collapsedAll[module] = true;
    });
    setCollapsed(collapsedAll);
  };

  return (
    <div className="container-fluid admin-module-page admin-role-permission-page py-4">
      <div className="card admin-surface-card admin-filter-card sticky-top mb-4">
        <div className="card-body admin-surface-body d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
          <div>
            <span className="admin-page-kicker">Access Control</span>
            <h3 className="admin-page-title fw-bold mb-1">
              Role Permission {roleName ? `- ${roleName}` : ""}
            </h3>
            <p className="admin-page-description mb-0">
              Review assigned permissions, search modules, and update the matrix.
            </p>
          </div>

          <div className="d-flex align-items-center gap-3 flex-wrap justify-content-lg-end">
            {hasChanges && (
              <span className="text-primary fw-semibold">
                * Unsaved Changes
              </span>
            )}

            <button
              className="btn btn-primary admin-page-action"
              onClick={save}
              disabled={!hasChanges}
              type="button"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>

      <div className="card admin-surface-card mb-4">
        <div className="card-header admin-surface-header d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0">Assigned Permissions</h5>
            <span className="badge bg-secondary">{selectedPerms.length}</span>
          </div>

          <button
            className="btn btn-sm btn-outline-danger"
            onClick={(e) => {
              e.stopPropagation();
              setSelected([]);
            }}
            type="button"
          >
            Remove All
          </button>
        </div>

        <div className="card-body">
          <button
            className="btn btn-outline-secondary w-100 mb-3 d-flex justify-content-between align-items-center"
            onClick={() => setShowAssigned((prev) => !prev)}
            type="button"
          >
            <span>Toggle Assigned Permissions</span>
            <span className="badge bg-light text-dark ms-2">
              {selectedPerms.length}
            </span>
          </button>

          <div className={`collapse ${showAssigned ? "show" : ""}`}>
            {selectedPerms.length === 0 ? (
              <div className="text-muted">No permissions assigned yet.</div>
            ) : (
              <div className="row g-2">
                {selectedPerms.map((p) => (
                  <div className="col-md-3 col-sm-6" key={p.intPermissionId}>
                    <div className="admin-tree-item p-2 d-flex align-items-center justify-content-between bg-light">
                      <div
                        style={{
                          flex: 1,
                          fontSize: "13px",
                          fontWeight: "500",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                        title={p.vchDescription}
                      >
                        {p.vchCode}
                      </div>

                      <button
                        className="btn btn-sm btn-outline-danger ms-2 flex-shrink-0"
                        onClick={() => toggle(p.intPermissionId)}
                        type="button"
                      >
                        ✖
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="admin-toolbar p-3 mb-4">
        <div className="row g-3 align-items-end">
          <div className="col-lg-8 col-md-7">
            <label className="form-label fw-semibold">Search permissions</label>
            <input
              className="form-control"
              placeholder="Search permissions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="col-lg-4 col-md-5">
            <div className="form-check form-switch d-flex align-items-center gap-2 mt-4">
              <input
                className="form-check-input"
                type="checkbox"
                checked={isAllSelected}
                onChange={handleGlobalToggle}
              />
              <label className="form-check-label">
                Select All
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          className="btn btn-outline-primary"
          onClick={handleExpandAll}
          type="button"
        >
          Expand All
        </button>

        <button
          className="btn btn-outline-secondary"
          onClick={handleCollapseAll}
          type="button"
        >
          Collapse All
        </button>
      </div>

      <div className="accordion" id="permissionAccordion">
        {Object.keys(grouped).map((module) => {
          const modulePerms = grouped[module];

          const filtered = modulePerms.filter((p) =>
            p.vchCode.toLowerCase().includes(search.toLowerCase())
          );

          if (!filtered.length) return null;

          const moduleSelected = modulePerms.every((p) =>
            selected.includes(p.intPermissionId)
          );

          return (
            <div className="card admin-matrix-panel mb-3 overflow-hidden" key={module}>
              <div className="card-header admin-surface-header d-flex justify-content-between align-items-center gap-3">
                <div className="d-flex align-items-center gap-3">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() =>
                      setCollapsed((prev) => ({
                        ...prev,
                        [module]: !prev[module]
                      }))
                    }
                    type="button"
                  >
                    {collapsed[module] ? "+" : "-"}
                  </button>

                  <h6 className="mb-0 fw-bold">{module}</h6>

                  <span className="badge bg-secondary">
                    {modulePerms.length}
                  </span>
                </div>

                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={moduleSelected}
                    onChange={() => handleModuleToggle(modulePerms)}
                  />
                </div>
              </div>

              {!collapsed[module] && (
                <div className="card-body">
                  <div className="row g-3">
                    {filtered.map((p) => {
                      const isChecked = selected.includes(p.intPermissionId);

                      return (
                        <div className="col-md-4 col-sm-6" key={p.intPermissionId}>
                          <div
                            className={`admin-tree-item p-2 d-flex justify-content-between align-items-center ${isChecked ? "bg-light border-primary" : ""
                              }`}
                          >
                            <span
                              style={{ fontSize: "13px", cursor: "pointer" }}
                              title={p.vchDescription}
                            >
                              {p.vchCode}
                            </span>

                            <div className="form-check form-switch m-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggle(p.intPermissionId)}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RolePermission;